import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabaseClient';
import styled from 'styled-components';
import { FaKey, FaSignOutAlt, FaUser, FaChartLine } from 'react-icons/fa';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #0a0e17;
  color: #ffffff;
`;

const Sidebar = styled.div`
  width: 250px;
  background-color: #121921;
  padding: 20px 0;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
`;

const SidebarHeader = styled.div`
  padding: 0 20px 20px;
  border-bottom: 1px solid #2a3a4a;
  margin-bottom: 20px;
  
  h1 {
    color: #00ff4c;
    font-size: 1.5rem;
    margin: 0 0 10px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }
  
  .user-name {
    font-weight: bold;
  }
  
  .user-role {
    font-size: 12px;
    color: #00ff4c;
  }
`;

const NavMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  a {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 15px 20px;
    color: #fff;
    text-decoration: none;
    transition: background-color 0.3s;
    
    &:hover, &.active {
      background-color: #1a2a3a;
    }
    
    svg {
      color: #00ff4c;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 30px;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  h1 {
    font-size: 28px;
    margin: 0;
  }
`;

const StatCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background-color: #121921;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  
  .stat-title {
    font-size: 14px;
    color: #aaa;
    margin-bottom: 10px;
  }
  
  .stat-value {
    font-size: 28px;
    font-weight: bold;
    color: #00ff4c;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 1px solid #ff3860;
  color: #ff3860;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #ff3860;
    color: white;
  }
`;

const AdminDashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalKeys: 0,
    activeKeys: 0,
    expiredKeys: 0
  });
  
  // Temel istatistikleri yükle
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Toplam anahtar sayısı
        const { count: totalCount } = await supabase
          .from('license_keys')
          .select('*', { count: 'exact', head: true });
        
        // Aktif anahtar sayısı
        const { count: activeCount } = await supabase
          .from('license_keys')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true);
        
        // Süresi dolmuş anahtar sayısı
        const now = new Date().toISOString();
        const { count: expiredCount } = await supabase
          .from('license_keys')
          .select('*', { count: 'exact', head: true })
          .lt('expiry_date', now);
        
        setStats({
          totalKeys: totalCount || 0,
          activeKeys: activeCount || 0,
          expiredKeys: expiredCount || 0
        });
      } catch (error) {
        console.error('İstatistikler yüklenirken hata:', error);
      }
    };
    
    fetchStats();
  }, []);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  return (
    <DashboardContainer>
      <Sidebar>
        <SidebarHeader>
          <h1>Kinfhertix</h1>
          <UserInfo>
            <img src={`https://cdn.discordapp.com/avatars/${currentUser?.id}/${currentUser?.avatar}.png`} alt="User Avatar" />
            <div>
              <div className="user-name">{currentUser?.username}</div>
              <div className="user-role">Admin</div>
            </div>
          </UserInfo>
        </SidebarHeader>
        
        <NavMenu>
          <NavItem>
            <Link to="/admin" className="active">
              <FaChartLine /> Dashboard
            </Link>
          </NavItem>
          <NavItem>
            <Link to="/admin/keys">
              <FaKey /> Lisans Anahtarları
            </Link>
          </NavItem>
        </NavMenu>
      </Sidebar>
      
      <Content>
        <DashboardHeader>
          <h1>Admin Dashboard</h1>
          <LogoutButton onClick={handleLogout}>
            <FaSignOutAlt /> Çıkış Yap
          </LogoutButton>
        </DashboardHeader>
        
        <StatCards>
          <StatCard>
            <div className="stat-title">Toplam Lisans Anahtarı</div>
            <div className="stat-value">{stats.totalKeys}</div>
          </StatCard>
          <StatCard>
            <div className="stat-title">Aktif Lisans Anahtarı</div>
            <div className="stat-value">{stats.activeKeys}</div>
          </StatCard>
          <StatCard>
            <div className="stat-title">Süresi Dolmuş Anahtarlar</div>
            <div className="stat-value">{stats.expiredKeys}</div>
          </StatCard>
        </StatCards>
        
        <div className="dashboard-actions">
          <h2>Hızlı İşlemler</h2>
          <Link to="/admin/keys" className="btn primary">Lisans Anahtarları Yönet</Link>
        </div>
      </Content>
    </DashboardContainer>
  );
};

export default AdminDashboard; 